/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class SlotMappingModel
 * @hideconstructor
 * @classdesc Provides access to the slot mapping model
 */
define([], () => {
	return class SlotMappingModel {

		constructor(slotMappingModelJson) {
			this._slotMappingModel = slotMappingModelJson;
			if (!this._slotMappingModel.slots){
				this._slotMappingModel.slots = [];
			}
			if (!this._slotMappingModel.multiMeasureGroups) {
				this._slotMappingModel.multiMeasureGroups = {};
			}
		}

		getSlotList() {
			return this._slotMappingModel.slots;
		}

		// Remove a slot from the model
		removeSlot(slotId){
			this._slotMappingModel.slots = this._slotMappingModel.slots.filter(slot => {
				return slot.name !== slotId;
			});
		}

		// Get a slot with a given id. If one does not exist, it will be created
		getSlot(slotId){
			let slot;
			for (let i = 0; i < this._slotMappingModel.slots.length; i++){
				if(this._slotMappingModel.slots[i].name === slotId ) {
					slot = this._slotMappingModel.slots[i];
					break;
				}
			}
			if(!slot){
				slot = {
					name: slotId
				};
				this._slotMappingModel.slots.push(slot);
			}
			if (!slot.dataItems){
				slot.dataItems = [];
			}
			return slot;
		}

		_getMultiMeasureGroup(dataset) {
			return this._slotMappingModel.multiMeasureGroups[dataset];
		}

		_initMultiMeasureGroup(dataset) {
			if (!this._slotMappingModel.multiMeasureGroups[dataset]) {
				this._slotMappingModel.multiMeasureGroups[dataset] = {};
			}
		}

		getMultiMeasureSlotId(dataset) {
			const group = this._getMultiMeasureGroup(dataset);
			return group && group.slotId;
		}
		setMultiMeasureSlotId(dataset, slotId) {
			this._initMultiMeasureGroup(dataset);
			const group = this._getMultiMeasureGroup(dataset);
			group.slotId = slotId;
		}

		getMultiMeasurePosition(dataset) {
			const group = this._getMultiMeasureGroup(dataset);
			return group ? group.position : -1;
		}
		setMultiMeasurePosition(dataset, position) {
			this._initMultiMeasureGroup(dataset);
			const group = this._getMultiMeasureGroup(dataset);
			group.position = position;
		}

		getMappedDataItemIdList(slotId) {
			let dataItemIds = [];
			this._slotMappingModel.slots.forEach(slot => {
				if(slot.dataItems && (!slotId || slotId === slot.name)){
					dataItemIds = dataItemIds.concat(slot.dataItems);
				}
			});
			return dataItemIds;
		}

		// Get the slot that is mapped to a given dataitem
		getMappedSlot(dataItemId) {
			let slot;
			for (let i = 0; i < this._slotMappingModel.slots.length; i++){
				const dataItems = this._slotMappingModel.slots[i].dataItems;
				if (dataItems){
					for (let j = 0; j < dataItems.length; j++){
						if (dataItems[j] === dataItemId) {
							slot = this._slotMappingModel.slots[i];
							break;
						}
					}
				}
				if (slot){
					break;
				}
			}
			return slot;
		}

		// Add a dataItem mapping to a given slot
		mapDataItemsToSlot(dataItemIdList, slotId, index = -1){
			this.unmapDataItemsFromSlots(dataItemIdList);
			const slot = this.getSlot(slotId);
			if (index < 0){
				slot.dataItems.push(...dataItemIdList);
			} else {
				slot.dataItems.splice(index, 0, ...dataItemIdList);
			}
		}

		// Add a dataItem mapping to a given slot
		unmapDataItemsFromSlots(dataItemIdList){
			dataItemIdList.forEach((dataItemId) => {
				const currentMappedSlot = this.getMappedSlot(dataItemId);
				if(currentMappedSlot){
					currentMappedSlot.dataItems = currentMappedSlot.dataItems.filter(id => {
						return dataItemId !== id;
					});
					if(currentMappedSlot.dataItems.length === 0){
						this.removeSlot(currentMappedSlot.name);
					}
				}
			});
		}


	};
});
